import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

const LockBodyContext = createContext({
  locks: 0,
  onLock: () => {
    console.error("LockBodyContext.onLock called outside Provider");
  },
  onUnlock: () => {
    console.error("LockBodyContext.onUnlock called outside Provider");
  },
});

export const LockBodyProvider = ({ children }: { children: ReactNode }) => {
  const [locks, setLocks] = useState(0);
  const Provider = LockBodyContext.Provider;

  const onLock = useCallback(() => setLocks((locks) => locks + 1), []);
  const onUnlock = useCallback(() => setLocks((locks) => locks - 1), []);

  const value = useMemo(
    () => ({ locks, onLock, onUnlock }),
    [locks, onLock, onUnlock],
  );

  return (
    <Provider value={value}>
      <LockBodyEffect />
      {children}
    </Provider>
  );
};

/**
 * Based on https://usehooks-ts.com/react-hook/use-locked-body
 *
 * Changes:
 *
 * * Addition of scroll restoration
 * * Setup with context
 */
export const LockBodyEffect = () => {
  const { locks } = useContext(LockBodyContext);
  const locked = locks > 0;

  // Do the side effect before render
  useLayoutEffect(() => {
    if (!locked) {
      return;
    }

    // Save initial body style
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;

    // Save initial scroll offset
    const originalScrollX = window.scrollX;
    const originalScrollY = window.scrollY;

    // Get the scrollBar width
    const root = document.body;

    const scrollBarWidth = root ? window.innerWidth - root.clientWidth : 0;

    // Lock body scroll
    document.body.style.overflow = "hidden";

    // Avoid width reflow
    if (scrollBarWidth) {
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    }

    return () => {
      document.body.style.overflow = originalOverflow;

      if (scrollBarWidth) {
        document.body.style.paddingRight = originalPaddingRight;
      }

      // scroll back to original position
      window.scrollTo(originalScrollX, originalScrollY);
    };
  }, [locked]);

  return null;
};

export function useLockedBody(locked: boolean) {
  const { onLock, onUnlock } = useContext(LockBodyContext);

  useEffect(() => {
    if (!locked) return;

    onLock();

    return () => onUnlock();
  }, [locked, onLock, onUnlock]);
}
